import { Fragment, ReactNode, useMemo, useState } from 'react';
import { LuSquarePen } from 'react-icons/lu';

import { BillingExclusion, BillingGroupAccount } from '@ndvr/billing-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { HintLabel } from '@/components/HintLabel';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { useAssetsByScIdsMap } from '@/hooks/market-data-queries';

import { useBillingExclusions, useCashEquivalentShareClasses } from './billing-exclusion-queries';
import { EditExclusionsDialog } from './EditExclusionsDialog';

const formatDate = (date?: string) => (date ? date.slice(0, 10) : undefined);

const billingPeriod = (account: BillingGroupAccount): string => {
    const start = formatDate(account.billingStartDate);
    const end = formatDate(account.billingEndDate);
    if (!start && !end) return '—';
    return `${start ?? '…'} – ${end ?? '…'}`;
};

// Read-only summary of an account's exclusions; editing lives in EditExclusionsDialog.
const ExclusionSummary = ({
    exclusion,
    assetByScId,
    cashEquivalentShareClassIds,
    exclusionDataLoading,
}: {
    exclusion: BillingExclusion | undefined;
    assetByScId: Map<string, AssetInfo>;
    cashEquivalentShareClassIds: string[];
    exclusionDataLoading: boolean;
}) => {
    // Spinner while the exclusion data loads, so we don't flash "—" or raw share class ids.
    if (exclusionDataLoading) {
        return <Spinner size="sm" />;
    }
    if (!exclusion || (!exclusion.excludedCash && exclusion.excludedShareClassIds.length === 0)) {
        return <>—</>;
    }

    const items: ReactNode[] = [];
    if (exclusion.excludedCash) {
        const cashFunds = cashEquivalentShareClassIds
            .map((scId) => assetByScId.get(scId)?.symbol ?? scId)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        items.push(
            <HintLabel key="cash" hint={cashFunds.length > 0 ? cashFunds.join(', ') : undefined}>
                Cash equivalents
            </HintLabel>,
        );
    }
    exclusion.excludedShareClassIds.forEach((scId) => {
        const asset = assetByScId.get(scId);
        items.push(
            <HintLabel key={scId} hint={asset?.description}>
                {asset?.symbol ?? scId}
            </HintLabel>,
        );
    });

    return (
        <span className="inline-flex flex-wrap items-center gap-x-1">
            {items.map((item, i) => (
                <Fragment key={i}>
                    {i > 0 && <span className="text-muted-foreground/50">·</span>}
                    {item}
                </Fragment>
            ))}
        </span>
    );
};

interface BillingGroupAccountsTableProps {
    accounts: BillingGroupAccount[];
    nameByGUID: Map<string, string>;
    namesLoading: boolean;
    canManage: boolean;
    className?: string;
}

export const BillingGroupAccountsTable = ({
    accounts,
    nameByGUID,
    namesLoading,
    canManage,
    className = 'px-12 py-3',
}: BillingGroupAccountsTableProps) => {
    const nameFor = (accountGUID?: string) => (accountGUID ? (nameByGUID.get(accountGUID) ?? accountGUID) : '—');

    const accountGUIDs = useMemo(() => accounts.map((a) => a.accountGUID), [accounts]);
    const { data: exclusionsByAccount, isLoading: exclusionsLoading } = useBillingExclusions(accountGUIDs);
    const { data: cashEquivalentShareClassIds = [] } = useCashEquivalentShareClasses();

    // Every share class id shown in this group: excluded across all accounts + cash equivalents.
    const scIds = useMemo(() => {
        const ids = new Set<string>(cashEquivalentShareClassIds);
        exclusionsByAccount?.forEach((exclusion) => exclusion.excludedShareClassIds.forEach((id) => ids.add(id)));
        return [...ids];
    }, [exclusionsByAccount, cashEquivalentShareClassIds]);
    const { data: assetByScId, isLoading: assetsLoading } = useAssetsByScIdsMap(scIds);
    const exclusionDataLoading = exclusionsLoading || assetsLoading;

    const [editingAccount, setEditingAccount] = useState<BillingGroupAccount | null>(null);

    // Account names resolve from a separate query; show a spinner until it's
    // loaded so rows don't briefly render raw GUIDs.
    if (namesLoading) {
        return (
            <div className={className}>
                <Spinner size="sm" />
            </div>
        );
    }

    const sortedAccounts = [...accounts].sort((a, b) =>
        nameFor(a.accountGUID).localeCompare(nameFor(b.accountGUID), undefined, { sensitivity: 'base' }),
    );

    return (
        <div className={className}>
            <table className="w-full table-fixed text-sm">
                <colgroup>
                    <col className="w-[28%]" />
                    <col className="w-[22%]" />
                    <col className="w-[20%]" />
                    <col className={canManage ? 'w-[26%]' : 'w-[30%]'} />
                    {canManage && <col className="w-[4%]" />}
                </colgroup>
                <thead>
                    <tr className="text-muted-foreground text-xs">
                        <th className="py-1 pr-4 text-left font-medium tracking-wide uppercase">Account</th>
                        <th className="py-1 pr-4 text-left font-medium tracking-wide uppercase">Settlement account</th>
                        <th className="py-1 pr-4 text-left font-medium tracking-wide uppercase">Billing period</th>
                        <th className="py-1 pr-4 text-left font-medium tracking-wide uppercase">Exclusions</th>
                        {canManage && <th className="py-1" />}
                    </tr>
                </thead>
                <tbody>
                    {sortedAccounts.map((account) => (
                        <tr key={account.accountGUID} className="group border-t border-gray-200">
                            <td className="py-2 pr-4 font-medium">{nameFor(account.accountGUID)}</td>
                            <td className="py-2 pr-4">{nameFor(account.settlementAccountGUIDOverride)}</td>
                            <td className="py-2 pr-4">{billingPeriod(account)}</td>
                            <td className="text-muted-foreground py-2 pr-4">
                                <ExclusionSummary
                                    exclusion={exclusionsByAccount?.get(account.accountGUID)}
                                    assetByScId={assetByScId}
                                    cashEquivalentShareClassIds={cashEquivalentShareClassIds}
                                    exclusionDataLoading={exclusionDataLoading}
                                />
                            </td>
                            {canManage && (
                                <td className="py-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                                        aria-label={`Edit exclusions for ${nameFor(account.accountGUID)}`}
                                        onClick={() => setEditingAccount(account)}
                                    >
                                        <LuSquarePen className="h-4 w-4" />
                                    </Button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            {editingAccount && (
                <EditExclusionsDialog
                    accountGUID={editingAccount.accountGUID}
                    accountName={nameFor(editingAccount.accountGUID)}
                    open={!!editingAccount}
                    onOpenChange={(open) => !open && setEditingAccount(null)}
                />
            )}
        </div>
    );
};
